import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  private _isCollapsed$ = new BehaviorSubject<boolean>(false);
  private _isMobileOpen$ = new BehaviorSubject<boolean>(false);
  private _isMobile$ = new BehaviorSubject<boolean>(false);

  public get isCollapsed$(): Observable<boolean> {
    return this._isCollapsed$.asObservable();
  }

  public get isMobileOpen$(): Observable<boolean> {
    return this._isMobileOpen$.asObservable();
  }

  public get isMobile$(): Observable<boolean> {
    return this._isMobile$.asObservable();
  }

  public get isCollapsed(): boolean {
    return this._isCollapsed$.value;
  }

  public get isMobileOpen(): boolean {
    return this._isMobileOpen$.value;
  }

  public get isMobile(): boolean {
    return this._isMobile$.value;
  }

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {
    // Breakpoint < 992px covers mobile phones and small tablets
    this.breakpointObserver
      .observe(['(max-width: 991.98px)'])
      .subscribe((result) => {
        const isMobile = result.matches;
        this._isMobile$.next(isMobile);
        if (!isMobile && this.isMobileOpen) {
          // Resized to desktop: dismiss mobile overlay
          this._isMobileOpen$.next(false);
        }
      });

    // Automatically close sidebar when navigating to a new route on mobile
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobile && this.isMobileOpen) {
          this.closeMobile();
        }
      });

    // Prevent background scrolling when mobile drawer is open
    this._isMobileOpen$.subscribe((isOpen) => {
      if (typeof document !== 'undefined') {
        if (this.isMobile && isOpen) {
          document.body.classList.add('sidebar-open-no-scroll');
        } else {
          document.body.classList.remove('sidebar-open-no-scroll');
        }
      }
    });
  }

  public toggle(): void {
    if (this.isMobile) {
      this._isMobileOpen$.next(!this._isMobileOpen$.value);
    } else {
      this._isCollapsed$.next(!this._isCollapsed$.value);
    }
  }

  public openMobile(): void {
    this._isMobileOpen$.next(true);
  }

  public closeMobile(): void {
    this._isMobileOpen$.next(false);
  }

  public setCollapsed(collapsed: boolean): void {
    this._isCollapsed$.next(collapsed);
  }
}
